import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { z } from "zod";

const authSchema = z.object({
  email: z.string().email("Geçersiz e-posta adresi"),
  password: z.string().min(6, "Şifre en az 6 karakter olmalıdır"),
  fullName: z.string().optional(),
});

const Auth = () => {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate("/");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        navigate("/");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate input
      const validation = authSchema.safeParse({
        email: email.trim(),
        password,
        fullName: fullName.trim(),
      });

      if (!validation.success) {
        toast.error(validation.error.issues[0].message);
        setLoading(false);
        return;
      }

      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email: validation.data.email,
          password: validation.data.password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              full_name: validation.data.fullName || "",
            },
          },
        });

        if (error) {
          if (error.message.includes("already registered")) {
            toast.error("Bu e-posta adresi zaten kayıtlı. Lütfen giriş yapın.");
          } else {
            toast.error(error.message);
          }
        } else {
          toast.success("Kayıt başarılı! Şimdi giriş yapabilirsiniz.");
          setIsSignUp(false);
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: validation.data.email,
          password: validation.data.password,
        });

        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            toast.error("Geçersiz giriş bilgileri. Lütfen e-posta ve şifrenizi kontrol edin.");
          } else {
            toast.error(error.message);
          }
        } else {
          toast.success("Giriş başarılı!");
          navigate("/");
        }
      }
    } catch (error) {
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-golden-yellow flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md bg-dark-red rounded-3xl p-8 shadow-2xl border-4 border-golden-yellow">
        <h1 className="text-5xl font-black text-center mb-2 uppercase tracking-tight text-golden-yellow">
          {isSignUp ? "Kayıt Ol" : "Giriş Yap"}
        </h1>
        <p className="text-center text-cream mb-8 font-semibold">
          {isSignUp
            ? "Hesabını oluştur"
            : "Tekrar hoş geldin!"}
        </p>

        <form onSubmit={handleAuth} className="space-y-6">
          {isSignUp && (
            <div>
              <label className="block text-golden-yellow font-bold text-sm mb-2 uppercase">
                İsim
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-golden-yellow bg-cream text-dark-red font-semibold focus:outline-none focus:ring-4 focus:ring-golden-yellow/50"
                placeholder="Adınız"
              />
            </div>
          )}

          <div>
            <label className="block text-golden-yellow font-bold text-sm mb-2 uppercase">
              E-Posta
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-golden-yellow bg-cream text-dark-red font-semibold focus:outline-none focus:ring-4 focus:ring-golden-yellow/50"
              placeholder="email@ornek.com"
              required
            />
          </div>

          <div>
            <label className="block text-golden-yellow font-bold text-sm mb-2 uppercase">
              Şifre
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-golden-yellow bg-cream text-dark-red font-semibold focus:outline-none focus:ring-4 focus:ring-golden-yellow/50"
              placeholder="••••••••"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-golden-yellow text-dark-red font-black text-lg py-6 rounded-2xl uppercase tracking-wide hover:bg-golden-yellow/90 border-4 border-cream"
          >
            {loading
              ? "Yükleniyor..."
              : isSignUp
              ? "Kayıt Ol"
              : "Giriş Yap"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-golden-yellow font-bold hover:text-cream transition-colors"
          >
            {isSignUp
              ? "Zaten hesabın var mı? Giriş yap"
              : "Hesabın yok mu? Kayıt ol"}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Auth;
